import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Phone, Leaf } from 'lucide-react'
import { navigation, companyInfo } from '../data/pages'

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => { setIsMobileMenuOpen(false) }, [location])

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-earth-900/5' : 'bg-transparent'
    }`}>
      <div className={`hidden lg:block transition-all duration-300 ${
        isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-10 opacity-100'
      }`}>
        <div className="bg-blossom-900 text-blossom-100 text-xs">
          <div className="container-wide section-padding flex items-center justify-between h-10">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{companyInfo.contact.phone}</span>
              <span>{companyInfo.hours}</span>
            </div>
            <div className="flex items-center gap-1.5"><Leaf className="w-3 h-3" />Carbon-Neutral Operations</div>
          </div>
        </div>
      </div>

      <nav className="container-wide section-padding">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/images/logo.png"
              alt="Blossom Prime Facility Solutions"
              className="h-10 lg:h-12 w-auto object-contain"
            />
            <div className="hidden sm:block">
              <span className={`text-lg lg:text-xl font-bold leading-tight transition-colors duration-500 ${isScrolled ? 'text-earth-900' : 'text-white'}`}>
                Blossom<span className={`transition-colors duration-500 ${isScrolled ? 'text-blossom-600' : 'text-blossom-400'}`}>Prime</span>
              </span>
              <span className={`block text-[10px] lg:text-xs tracking-widest uppercase transition-colors duration-500 ${isScrolled ? 'text-earth-500' : 'text-earth-300'}`}>Facility Solutions</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link key={item.href} to={item.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  location.pathname === item.href
                    ? isScrolled ? 'bg-blossom-50 text-blossom-700' : 'bg-white/15 text-white'
                    : isScrolled ? 'text-earth-600 hover:text-blossom-700 hover:bg-blossom-50/50' : 'text-earth-200 hover:text-white hover:bg-white/10'
                }`}>
                {item.label}
              </Link>
            ))}
            <Link to="/contact" className="ml-4 btn-primary text-sm">Get a Quote</Link>
          </div>

          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'hover:bg-earth-100 text-earth-800' : 'hover:bg-white/10 text-white'}`}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-earth-100 overflow-hidden">
            <div className="section-padding py-6 space-y-2">
              {navigation.map((item) => (
                <Link key={item.href} to={item.href}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === item.href ? 'bg-blossom-50 text-blossom-700' : 'text-earth-700 hover:bg-earth-50'
                  }`}>
                  {item.label}
                </Link>
              ))}
              <Link to="/contact" className="block btn-primary mt-4 text-center">Get a Quote</Link>
              <div className="pt-4 border-t border-earth-100 text-sm text-earth-500 space-y-2">
                <p className="flex items-center gap-2"><Phone className="w-4 h-4" />{companyInfo.contact.phone}</p>
                <p>{companyInfo.hours}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
